import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3, BookOpen, CalendarCheck, Check, ChevronDown, Chrome, ClipboardCheck, Clock, CreditCard,
  Eye, FileCheck2, FileSpreadsheet, FileText, KeyRound, Megaphone, Mail, MessageCircle,
  Printer, ScanSearch, ShieldCheck, Users, Zap,
} from "lucide-react";

const EMAIL = "rasedhudury@gmail.com";
const WHATSAPP = "https://wa.me/966553691551";

const NAV_LINKS = [
  { label: "عن المنصة والفوائد", href: "#about", testId: "nav-link-about" },
  { label: "خطط الاشتراك والأسعار", href: "#pricing", testId: "nav-link-pricing" },
  { label: "المميزات الرئيسية", href: "#features", testId: "nav-link-features" },
  { label: "إضافة كروم", href: "#chrome-extension", testId: "nav-link-extension" },
  { label: "الأسئلة الشائعة", href: "#faq", testId: "nav-link-faq" },
  { label: "الدعم والتواصل", href: "#contact", testId: "nav-link-contact" },
];

const KPIS = [
  { value: "100%", label: "توافق تام مع معايير وزارة التعليم ونظام حضوري", testId: "kpi-compliance" },
  { value: "3 ثوانٍ", label: "لسحب جدول الحضور الكامل عبر إضافة كروم المدمجة", testId: "kpi-speed" },
  { value: "A4 رسمية", label: "خطابات مساءلة وتعهدات جاهزة للتوقيع والطباعة", testId: "kpi-letters" },
  { value: "صفر خطأ", label: "في حساب دقائق التأخير وفترات السماح المحددة", testId: "kpi-accuracy" },
];

const BENEFITS = [
  { icon: Clock, title: "توفير 4+ ساعات عمل يومياً", desc: "إلغاء المطابقة اليدوية الشاقة بين سجلات الحضور والانصراف وقوائم المعلمين والإداريين." },
  { icon: Chrome, title: "إضافة كروم الذكية لنظام حضوري", desc: "سحب فوري لكافة الجداول مع التنقل التلقائي بين الصفحات وإرسال مباشر للمنصة." },
  { icon: FileCheck2, title: "خطابات مساءلة وزارية في ثوانٍ", desc: "توليد خطابات التعهد والإنذار والمساءلة الرسمية على صفحة واحدة A4 ببيانات الموظف." },
  { icon: FileSpreadsheet, title: "استيراد مرن لملفات إكسل", desc: "ربط ذكي للأعمدة والأسماء تلقائياً، مع دعم التاريخ الهجري وقواعد تطبيع أوقات الدوام." },
];

const PLANS = [
  {
    id: "trial", name: "اشتراك تجريبي", badge: "مجاني تماماً", price: "مجاناً", period: "3 أيام تجربة كاملة",
    desc: "تجربة حقيقية لكافة إمكانيات المنصة وإضافة كروم بدون أي التزام مالي مسبق.",
    features: ["وصول كامل لكل الميزات", "أداة إضافة كروم «راصد»", "توليد وطباعة المساءلات الرسمية A4",
      "بوابة الموظفين وتقييم الأداء", "دعم فني فوري أثناء التجربة"],
    cta: "ابدأ التجربة المجانية", highlighted: false, testId: "plan-card-trial",
  },
  {
    id: "basic", name: "الأساسية", badge: "انطلاقة ذكية", price: "50 ريال", period: "فصل دراسي",
    desc: "النواة التشغيلية الكاملة لمتابعة الحضور والمساءلة في المدرسة.",
    features: ["سجل الحضور والانصراف كاملاً", "استيراد حضوري عبر إضافة كروم وملفات إكسل",
      "إدارة بيانات الموظفين", "المساءلات وقرارات الحسم توليداً وطباعة", "تقارير PDF رسمية بالتاريخ الهجري"],
    cta: "اشترك بالأساسية", highlighted: false, testId: "plan-card-basic",
  },
  {
    id: "premium", name: "البريميوم", badge: "الأنسب لمعظم المدارس", price: "100 ريال", period: "فصل دراسي",
    desc: "كل مزايا الأساسية إضافة إلى بوابة الموظفين التفاعلية والأدوات الإدارية.",
    features: ["كل مزايا الباقة الأساسية", "بوابة الموظفين: إرسال المساءلات والرد عليها إلكترونياً",
      "التعاميم بالتوقيع الإلكتروني", "الصلاحيات وتفويض الموظفين", "سجل التدقيق"],
    cta: "اشترك بالبريميوم", highlighted: true, testId: "plan-card-premium",
  },
  {
    id: "semester", name: "الشاملة (فصل)", badge: "شمولية كاملة", price: "150 ريال", period: "فصل دراسي",
    desc: "كل مزايا البريميوم مع منظومة تقييم الأداء الوظيفي بالنماذج الرسمية.",
    features: ["كل مزايا الباقة البريميوم", "تقييم الأداء الوظيفي بالنماذج الرسمية مع الشواهد",
      "نقاط المفاضلة وترتيب المعلمين", "لوحات متابعة التقييم للمدير"],
    cta: "اشترك بالشاملة", highlighted: false, testId: "plan-card-semester",
  },
  {
    id: "year", name: "الشاملة (عام)", badge: "أفضل قيمة وأعلى وفر", price: "250 ريال", period: "عام دراسي كامل",
    desc: "ميزات الشاملة نفسها لكن للعام الدراسي كاملاً بأعلى وفر.",
    features: ["كل مزايا الشاملة الفصلية", "صلاحية ممتدة للعام الدراسي كاملاً",
      "أرشفة دائمة للسجلات والتقارير طوال العام", "أولوية قصوى في الدعم الفني"],
    cta: "اشترك للعام الكامل", highlighted: false, testId: "plan-card-year",
  },
];

const FEATURES = [
  { icon: CalendarCheck, tag: "رصد يومي", title: "تتبع الحضور والغياب", desc: "رصد وتوثيق حضور وغياب منسوبي المدرسة يومياً مع تقارير تفصيلية فورية." },
  { icon: Chrome, tag: "تقنية حصرية", title: "أداة الكروم", desc: "امتداد متصفح متخصص لاستيراد بيانات الحضور والغياب من نظام نور مباشرة إلى راصد بضغطة واحدة." },
  { icon: FileText, tag: "وفق لوائح الوزارة", title: "المساءلات الإلكترونية", desc: "إنشاء وإرسال المساءلات الرسمية إلكترونياً مع تتبع حالة كل مساءلة." },
  { icon: Megaphone, tag: "توقيع إلكتروني", title: "التعاميم الرسمية", desc: "إصدار التعاميم وتتبع من وقّع على استلامها." },
  { icon: Users, tag: "دخول بالهوية", title: "بوابة الموظفين", desc: "بوابة إلكترونية خاصة يدخل إليها كل موظف للرد على المساءلات بتوقيع إلكتروني والاطلاع على التعاميم.", hot: true },
  { icon: KeyRound, tag: "تحكم كامل", title: "إدارة الصلاحيات", desc: "تفويض الموظفين وتحديد صلاحياتهم على تبويبات النظام من حساب المدير مباشرة." },
  { icon: ClipboardCheck, tag: "نماذج رسمية", title: "تقييم الأداء الوظيفي", desc: "نظام متكامل لتقييم أداء جميع منسوبي المدرسة وفق النماذج الرسمية لكل مسمى وظيفي مع التقييم الذاتي ورفع الشواهد ومقارنة التقييمين.", hot: true },
  { icon: BarChart3, tag: "جاهزة للطباعة", title: "التقارير والإحصاءات", desc: "تقارير شاملة قابلة للطباعة لجميع جوانب أداء المدرسة." },
  { icon: CreditCard, tag: "تفعيل فوري", title: "الدفع الإلكتروني", desc: "اشتراك سهل عبر بطاقة البنك أو التحويل البنكي." },
];

const FAQS = [
  { q: "كيف تعمل إضافة كروم «راصد حضوري» مع منصة حضوري؟", a: "تُثبّت الإضافة في متصفح جوجل كروم، وعند فتحك صفحة سجلات الحضور في منصة حضوري الرسمية تضغط زر السحب، فتقرأ الأداة الجدول وتتنقل آلياً بين جميع الصفحات وتنقل البيانات مباشرة لحسابك في راصد خلال ثوانٍ معدودة." },
  { q: "هل خطابات المساءلة متوافقة مع الأنظمة واللوائح التعليمية الرسمية؟", a: "نعم، صُممت نماذج المساءلات والتعهدات لتطابق الصيغ الإدارية المعتمدة في المدارس السعودية، وتُطبع بالكامل على صفحة A4 واحدة تتضمن بيانات الموظف وتاريخ الواقعة ودقائق التأخير ومكان التوقيع والإفادة." },
  { q: "ما الفرق بين الباقات الأربع؟", a: "الأساسية (50 ريالاً) تغطي الحضور والمساءلات والحسم وإدارة الموظفين لفصل دراسي، والبريميوم (100 ريال) تضيف بوابة الموظفين والتعاميم والصلاحيات وسجل التدقيق، والشاملة (150 ريالاً) تضيف تقييم الأداء الوظيفي، والشاملة السنوية (250 ريالاً) تمنح نفس الميزات للعام الدراسي كاملاً بأعلى وفر." },
  { q: "هل أحتاج إلى إدخال بطاقة ائتمانية لبدء التجربة المجانية؟", a: "لا على الإطلاق، التجربة المجانية لمدة 3 أيام متاحة فور التسجيل مباشرة بدون طلب أي بيانات دفع أو بطاقة بنكية." },
  { q: "هل يمكنني استيراد البيانات من ملف إكسل إذا لم أستخدم إضافة كروم؟", a: "بالتأكيد، تدعم المنصة رفع ملفات إكسل بصيغتي xlsx وxls مع محرك ربط ذكي يكتشف أعمدة الأسماء والأوقات تلقائياً ويتعامل مع التواريخ الهجرية والميلادية." },
  { q: "كيف يتم الدفع وتفعيل الاشتراك؟", a: "الدفع إلكتروني بالبطاقة (مدى / فيزا / ماستركارد) عبر بوابة Moyasar الآمنة مباشرة داخل صفحة الاشتراك، ويُفعَّل حسابك تلقائياً فور نجاح الدفع بدون أي انتظار." },
];

const SectionTitle = ({ overline, title, sub }) => (
  <div className="text-center max-w-2xl mx-auto mb-12">
    <p className="text-xs font-semibold tracking-widest text-amber-600 mb-3">{overline}</p>
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-emerald-950">{title}</h2>
    {sub && <p className="text-base text-slate-600 leading-relaxed mt-4">{sub}</p>}
  </div>
);

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen bg-[#F8FAF9] scroll-smooth" data-testid="landing-page">
      {/* ===== Sticky Glass Navbar ===== */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-emerald-950/10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="landing-navbar-logo">
            <div className="w-10 h-10 rounded-xl bg-emerald-900 flex items-center justify-center">
              <ScanSearch className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="font-extrabold text-emerald-950 leading-4">راصد حضوري</p>
              <p className="text-[11px] text-slate-400">منصة تحليلات الحضور الذكية للمدارس</p>
            </div>
          </div>
          <nav className="hidden lg:flex items-center gap-5">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={l.testId}
                className="text-sm text-slate-600 hover:text-emerald-900 font-medium transition-colors">{l.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-btn-login"
              className="text-emerald-900 hover:text-emerald-950 font-semibold px-4 py-2 border border-emerald-200 hover:border-emerald-300 rounded-xl text-sm transition-colors">
              تسجيل الدخول
            </Link>
            <Link to="/subscribe" data-testid="nav-btn-subscribe"
              className="bg-emerald-900 hover:bg-emerald-950 text-white font-bold px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg text-sm transition-transform active:scale-95">
              ابدأ الآن
            </Link>
          </div>
        </div>
      </header>

      {/* ===== 1) Hero / About ===== */}
      <section id="about" className="scroll-mt-24 py-16 sm:py-24 lg:py-28 bg-gradient-to-b from-emerald-950 to-emerald-900 text-white" data-testid="section-about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="inline-block text-xs font-semibold tracking-widest text-amber-300 bg-emerald-900/60 border border-emerald-800 rounded-full px-4 py-1.5 mb-6" data-testid="hero-badge">
            المنظومة الرقمية المعتمدة لمدراء المدارس بالمملكة العربية السعودية
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-4xl mx-auto" style={{ lineHeight: 1.9 }} data-testid="hero-headline">
            وداعاً للتدقيق اليدوي: حوّل بيانات «منصة حضوري» إلى تقارير ومساءلات رسمية بضغطة زر واحدة
          </h1>
          <p className="text-lg sm:text-xl text-emerald-100/90 max-w-3xl mx-auto mt-7" style={{ lineHeight: 2 }} data-testid="hero-subheadline">
            منصة راصد حضوري الذكية صُممت خصيصاً لمدراء المدارس ووكلائهم لأتمتة سحب بيانات الحضور عبر إضافة كروم السريعة،
            احتساب دقائق التأخر والانصراف المبكر بدقة، وإصدار خطابات المساءلة الوزارية الرسمية بنموذج A4 جاهز للطباعة فوراً.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/subscribe" data-testid="hero-primary-cta"
              className="bg-amber-500 hover:bg-amber-400 text-emerald-950 font-extrabold px-7 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-transform active:scale-95">
              ابدأ الآن — تجربة مجانية 3 أيام
            </Link>
            <Link to="/login" data-testid="hero-secondary-cta"
              className="border border-emerald-700 hover:border-emerald-600 text-emerald-100 hover:text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">
              تسجيل الدخول
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
            {KPIS.map((k) => (
              <div key={k.testId} className="bg-emerald-900/50 border border-emerald-800 rounded-2xl p-5" data-testid={k.testId}>
                <p className="text-2xl sm:text-3xl font-extrabold text-amber-400">{k.value}</p>
                <p className="text-xs sm:text-sm text-emerald-100/80 leading-5 mt-2">{k.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits for principals */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="فوائد مباشرة للميدان التعليمي" title="ماذا تقدم راصد لمدير المدرسة؟"
            sub="أدوات صُممت بعناية لتخفيف العبء الإداري اليومي ورفع جودة الانضباط المدرسي" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white border rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`benefit-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-emerald-900" />
                </div>
                <h3 className="text-lg font-bold text-emerald-950 mb-2">{b.title}</h3>
                <p className="text-sm text-slate-600 leading-6">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 2) Pricing ===== */}
      <section id="pricing" className="scroll-mt-24 py-16 sm:py-24 bg-white border-y" data-testid="section-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="أسعار شفافة بدون رسوم خفية" title="خطط الاشتراك وأسعارها"
            sub="اختر ما يناسب مدرستك — وابدأ بالتجربة المجانية دون أي بيانات دفع" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch">
            {PLANS.map((p) => (
              <div key={p.id} data-testid={p.testId}
                className={`relative rounded-2xl p-6 sm:p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                  p.highlighted
                    ? "bg-emerald-950 text-white shadow-2xl ring-2 ring-amber-400 scale-[1.02]"
                    : "bg-[#F8FAF9] border hover:shadow-xl"}`}>
                <span className={`absolute -top-3 right-6 text-[11px] font-bold px-3 py-1 rounded-full ${
                  p.highlighted ? "bg-amber-400 text-emerald-950" : "bg-emerald-100 text-emerald-800"}`}>
                  {p.badge}
                </span>
                <h3 className={`text-xl font-bold ${p.highlighted ? "text-white" : "text-emerald-950"}`}>{p.name}</h3>
                <div className="flex items-end gap-2 mt-4">
                  <span className={`text-4xl font-extrabold ${p.highlighted ? "text-amber-400" : "text-emerald-900"}`} data-testid={`${p.testId}-price`}>{p.price}</span>
                  {p.period && <span className={`text-xs pb-1.5 ${p.highlighted ? "text-emerald-200" : "text-slate-500"}`}>{p.period}</span>}
                </div>
                <p className={`text-sm leading-6 mt-3 ${p.highlighted ? "text-emerald-100/85" : "text-slate-600"}`}>{p.desc}</p>
                <ul className="space-y-2.5 mt-6 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm leading-6 ${p.highlighted ? "text-emerald-50" : "text-slate-600"}`}>
                      <Check className={`w-4 h-4 mt-1 shrink-0 ${p.highlighted ? "text-amber-400" : "text-emerald-700"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to={`/subscribe?plan=${p.id}`} data-testid={`${p.testId}-cta`}
                  className={`mt-8 text-center font-bold px-5 py-3 rounded-xl transition-transform active:scale-95 ${
                    p.highlighted
                      ? "bg-amber-500 hover:bg-amber-400 text-emerald-950 shadow-lg"
                      : "bg-emerald-900 hover:bg-emerald-950 text-white"}`}>
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-10 max-w-2xl mx-auto bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center justify-center gap-4 text-center sm:text-right" data-testid="payment-info-card">
            <CreditCard className="w-6 h-6 text-emerald-800 shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-950">دفع إلكتروني آمن عبر Moyasar — مدى / فيزا / ماستركارد</p>
              <p className="text-xs text-emerald-700 mt-0.5">يُفعَّل اشتراكك تلقائياً وفورياً بمجرد نجاح الدفع — بدون انتظار أو إشعارات</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3) Features ===== */}
      <section id="features" className="scroll-mt-24 py-16 sm:py-24" data-testid="section-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="قدرات المنصة" title="المميزات الرئيسية للموقع"
            sub="منظومة متكاملة من أدوات الرصد والتحليل والمساءلة الإدارية" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div key={i} className={`bg-white border rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${f.hot ? "ring-2 ring-amber-400 border-amber-300" : ""}`} data-testid={`feature-card-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <f.icon className="w-5 h-5 text-emerald-900" />
                  </div>
                  <span className="flex items-center gap-1.5">
                    {f.hot && <span className="text-[11px] font-extrabold text-emerald-950 bg-amber-400 rounded-full px-2.5 py-1">الأبرز</span>}
                    <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">{f.tag}</span>
                  </span>
                </div>
                <h3 className="text-lg font-bold text-emerald-950 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-6">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Highlight: تقييم الأداء الوظيفي + بوابة الموظفين ===== */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 via-emerald-50/60 to-slate-50" data-testid="section-highlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="الأبرز في راصد" title="تقييم الأداء الوظيفي وبوابة الموظفين"
            sub="منظومتان متكاملتان تُغلقان حلقة التقويم والمتابعة — من المدير إلى الموظف وبالعكس" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="relative overflow-hidden bg-emerald-950 text-white rounded-3xl p-7 sm:p-9" data-testid="highlight-eval">
              <ClipboardCheck className="absolute -left-8 -bottom-8 w-44 h-44 text-emerald-800/40 pointer-events-none" />
              <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center mb-5">
                <ClipboardCheck className="w-6 h-6 text-emerald-950" />
              </div>
              <h3 className="text-xl sm:text-2xl font-extrabold mb-4">تقييم الأداء الوظيفي</h3>
              <ul className="space-y-2.5">
                {["نماذج تقييم رسمية لكل مسمى وظيفي (معلم، وكيل، إداري، موجه...)",
                  "تقييم ذاتي وتقييم مدير مع مقارنة آلية بينهما",
                  "رفع الشواهد (صور/PDF) ومراجعة كل شاهد على حدة",
                  "احتساب آلي للمعيار الأول من سجل الحضور والمساءلات",
                  "نقاط مفاضلة ترتّب المعلمين تنازلياً"].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-emerald-100/90 leading-6">
                    <Check className="w-4 h-4 mt-1 text-amber-400 shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <Link to="/subscribe" data-testid="highlight-eval-cta"
                className="inline-block mt-7 bg-amber-500 hover:bg-amber-400 text-emerald-950 font-extrabold px-6 py-3 rounded-xl shadow-lg transition-transform active:scale-95">
                جرّبها الآن مجاناً
              </Link>
            </div>
            <div className="relative overflow-hidden bg-emerald-950 text-white rounded-3xl p-7 sm:p-9" data-testid="highlight-portal">
              <Users className="absolute -left-8 -bottom-8 w-44 h-44 text-emerald-800/40 pointer-events-none" />
              <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center mb-5">
                <Users className="w-6 h-6 text-emerald-950" />
              </div>
              <h3 className="text-xl sm:text-2xl font-extrabold mb-4">بوابة الموظفين</h3>
              <ul className="space-y-2.5">
                {["دخول برقم الهوية ورمز المدرسة — بلا كلمات مرور",
                  "الرد على المساءلات بتوقيع إلكتروني",
                  "الاطلاع على التعاميم والتوقيع على استلامها",
                  "التقييم الذاتي ورفع الشواهد من الجوال مباشرة"].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-emerald-100/90 leading-6">
                    <Check className="w-4 h-4 mt-1 text-amber-400 shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <Link to="/subscribe" data-testid="highlight-portal-cta"
                className="inline-block mt-7 bg-amber-500 hover:bg-amber-400 text-emerald-950 font-extrabold px-6 py-3 rounded-xl shadow-lg transition-transform active:scale-95">
                جرّبها الآن مجاناً
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Chrome extension band ===== */}
      <section id="chrome-extension" className="scroll-mt-24 py-16 bg-emerald-950 text-white" data-testid="section-extension">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-10">
          <div className="flex-1">
            <p className="text-xs font-semibold tracking-widest text-amber-400 mb-3">إضافة كروم «راصد»</p>
            <h2 className="text-2xl sm:text-3xl font-bold">من صفحة حضوري إلى تقريرك الرسمي في ثوانٍ</h2>
            <ul className="space-y-3 mt-6">
              {["تسجيل دخول مرة واحدة بنفس بريدك وكلمة مرورك في المنصة",
                "سحب ذكي للجداول مع التنقل التلقائي بين جميع الصفحات",
                "إرسال فوري مشفر مع مؤشر تقدم مباشر بعدد الصفحات والسجلات",
                "قراءة صحيحة لعمود رقم الهوية وتجاهل الأعمدة التسلسلية"].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-emerald-100/90 leading-6">
                  <Zap className="w-4 h-4 mt-1 text-amber-400 shrink-0" />{t}
                </li>
              ))}
            </ul>
            <Link to="/subscribe" data-testid="extension-cta"
              className="inline-block mt-8 bg-amber-500 hover:bg-amber-400 text-emerald-950 font-extrabold px-6 py-3 rounded-xl shadow-lg transition-transform active:scale-95">
              جرّب الإضافة مجاناً الآن
            </Link>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4 w-full">
            {[{ icon: Chrome, t: "تثبيت بضغطة" }, { icon: ScanSearch, t: "قراءة ذكية" },
              { icon: Printer, t: "طباعة فورية" }, { icon: ShieldCheck, t: "تشفير كامل" }].map((c, i) => (
              <div key={i} className="bg-emerald-900/50 border border-emerald-800 rounded-2xl p-6 text-center" data-testid={`extension-mini-${i}`}>
                <c.icon className="w-7 h-7 text-amber-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-emerald-50">{c.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4) FAQ ===== */}
      <section id="faq" className="scroll-mt-24 py-16 sm:py-24" data-testid="section-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="إجابات واضحة" title="الأسئلة الشائعة" />
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="bg-white border rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-trigger-${i}`}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-right">
                  <span className="text-sm sm:text-base font-bold text-emerald-950">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-emerald-800 shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <p className="px-5 pb-5 text-sm text-slate-600 leading-7 border-t pt-4" data-testid={`faq-answer-${i}`}>{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5) Contact ===== */}
      <section id="contact" className="scroll-mt-24 py-16 sm:py-24 bg-white border-y" data-testid="section-contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="نخدمك طوال العام الدراسي" title="الدعم والتواصل"
            sub="فريق دعم متخصص يفهم طبيعة العمل الإداري المدرسي" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            <a href="/manager-guide.pdf" target="_blank" rel="noreferrer" download data-testid="contact-guide-card"
              className="bg-amber-50 border border-amber-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block">
              <BookOpen className="w-6 h-6 text-amber-600 mb-3" />
              <h3 className="font-bold text-emerald-950 mb-1">دليل المدير الإرشادي</h3>
              <p className="text-xs text-slate-500 mt-2">حمّل الدليل المصوّر (PDF) وتعرّف على كل مزايا المنصة خطوة بخطوة قبل الاشتراك</p>
            </a>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-card"
              className="bg-[#F0FDF4] border border-green-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block">
              <MessageCircle className="w-6 h-6 text-green-600 mb-3" />
              <h3 className="font-bold text-emerald-950 mb-1">تواصل معنا عبر واتساب</h3>
              <p className="text-xs text-slate-500 mt-2">يفتح محادثة واتساب مباشرة مع فريق الدعم</p>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-card"
              className="bg-[#F8FAF9] border rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block">
              <Mail className="w-6 h-6 text-emerald-900 mb-3" />
              <h3 className="font-bold text-emerald-950 mb-1">الدعم عبر البريد الإلكتروني</h3>
              <p dir="ltr" className="text-sm font-bold text-emerald-800 text-right">{EMAIL}</p>
              <p className="text-xs text-slate-500 mt-2">استجابة سريعة للاستفسارات الفنية والاشتراكات</p>
            </a>
            <div className="bg-[#F8FAF9] border rounded-2xl p-6" data-testid="contact-activation-card">
              <Zap className="w-6 h-6 text-emerald-900 mb-3" />
              <h3 className="font-bold text-emerald-950 mb-1">الدفع والتفعيل الفوري</h3>
              <p className="text-sm text-slate-600 leading-6">ادفع بالبطاقة مباشرة عند الاشتراك عبر بوابة Moyasar الآمنة ويُفعَّل حسابك تلقائياً دون أي انتظار</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 6+7) Privacy & Terms highlights ===== */}
      <section className="py-16" data-testid="section-legal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white border rounded-2xl p-6 sm:p-8" data-testid="privacy-highlight-card">
            <ShieldCheck className="w-6 h-6 text-emerald-900 mb-3" />
            <h3 className="text-xl font-bold text-emerald-950 mb-2">سياسة الخصوصية</h3>
            <p className="text-sm text-slate-600 leading-7">
              نلتزم بأعلى معايير حماية البيانات الإدارية والتعليمية. لا تُباع أو تُشارك أي بيانات مع أطراف ثالثة،
              ولا تقرأ إضافة كروم أي صفحات خارج منصة حضوري الرسمية.
            </p>
            <Link to="/privacy" data-testid="link-privacy-page"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800 hover:text-emerald-950 mt-4">
              اقرأ السياسة كاملة <Eye className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-white border rounded-2xl p-6 sm:p-8" data-testid="terms-highlight-card">
            <FileText className="w-6 h-6 text-emerald-900 mb-3" />
            <h3 className="text-xl font-bold text-emerald-950 mb-2">الشروط والأحكام</h3>
            <p className="text-sm text-slate-600 leading-7">
              تنظم شروط الخدمة استخدام منصة راصد وإضافة كروم، وسياسات الاشتراكات الفصلية والسنوية،
              وإجراءات الاستخدام المصرح بها للمدارس والهيئات التعليمية.
            </p>
            <Link to="/terms" data-testid="link-terms-page"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800 hover:text-emerald-950 mt-4">
              اقرأ الشروط كاملة <Eye className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 8) Footer ===== */}
      <footer className="bg-emerald-950 text-white border-t border-emerald-900" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-emerald-900 flex items-center justify-center">
                <ScanSearch className="w-4.5 h-4.5 text-amber-400" />
              </div>
              <p className="font-extrabold">منصة راصد حضوري</p>
            </div>
            <p className="text-sm text-emerald-100/75 leading-7">
              المنصة الذكية الرائدة في تحليل سجلات الحضور وإصدار خطابات المساءلة الإدارية لمدراء المدارس في المملكة العربية السعودية.
            </p>
          </div>
          <div>
            <p className="font-bold mb-4 text-amber-400 text-sm">روابط سريعة</p>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/login" data-testid="footer-link-login" className="text-emerald-100/80 hover:text-white transition-colors">تسجيل الدخول</Link></li>
              <li><Link to="/subscribe" data-testid="footer-link-subscribe" className="text-emerald-100/80 hover:text-white transition-colors">الاشتراك في المنصة</Link></li>
              <li><a href="#chrome-extension" data-testid="footer-link-extension" className="text-emerald-100/80 hover:text-white transition-colors">إضافة كروم «راصد»</a></li>
              <li><a href="#pricing" data-testid="footer-link-pricing" className="text-emerald-100/80 hover:text-white transition-colors">خطط الأسعار</a></li>
            </ul>
          </div>
          <div>
            <p className="font-bold mb-4 text-amber-400 text-sm">السياسات والدعم</p>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/privacy" data-testid="footer-link-privacy" className="text-emerald-100/80 hover:text-white transition-colors">سياسة الخصوصية</Link></li>
              <li><Link to="/terms" data-testid="footer-link-terms" className="text-emerald-100/80 hover:text-white transition-colors">الشروط والأحكام</Link></li>
              <li><a href="#contact" data-testid="footer-link-support" className="text-emerald-100/80 hover:text-white transition-colors">الدعم والتواصل</a></li>
            </ul>
          </div>
          <div>
            <p className="font-bold mb-4 text-amber-400 text-sm">الدفع والتفعيل</p>
            <p className="text-sm text-emerald-100/80 leading-7">
              دفع إلكتروني آمن بالبطاقة (مدى / فيزا / ماستركارد) عبر Moyasar مع تفعيل فوري للاشتراك.<br />
              البريد: <span dir="ltr">{EMAIL}</span>
            </p>
          </div>
        </div>
        <div className="border-t border-emerald-900 py-5 text-center text-xs text-emerald-100/60" data-testid="footer-copyright">
          جميع الحقوق محفوظة © 2026 لمنصة راصد حضوري الذكية — مصممة لخدمة الميدان التعليمي بالمملكة
        </div>
      </footer>
    </div>
  );
}
